import isempty from 'lodash/isEmpty';
import {reportError, reportSuccess} from '@homeaway/react-error-reporter';

import {pricingGraphql} from '../api/pricingGraphQl';
import {quoteGraphql} from '../api/quoteGraphQl';
import {log as logToServer} from '../api/logger';
import edapActions from '../actions/edapActions';
import listingActions from '../actions/listingActions';
import actions from '../actions/damagePolicyActions';
import cancellationPolicyActions from '../actions/cancellationPolicyActions';
import {priceErrorMap, types} from './errorEvents';
import {Map} from 'immutable';
import {evaluateExperiment} from '../utils/abacusHelper';
import {VRBO_PDP_R2D2_QUERY} from '../abacus/experiments';
import pricingHelperQueryComparator from './pricingHelperQueryComparator';
import {priceSummaryGraphql} from '../api/priceSummaryGraphQl';

function createDefaultPricingKey(tripData, systemData) {
    return new Map({
        arrival: tripData.arriveDateText,
        departure: tripData.departDateText,
        adults: tripData.adults,
        children: tripData.children,
        pets: tripData.pets,
        currency: systemData.currency
    });
}

function getError(checkoutError) {
    const errorCode = checkoutError ? checkoutError.errorCode : 'UNDEFINED';

    if (errorCode) {
        return priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;
    }

    return null;
}

function initializeChildAges(children) {
    let childAges = [];
    for (let i = 0; i < children; i++) {
        childAges.push(-1);
    }
    return childAges;
}

/* eslint-disable complexity */
function handleError(store, pageLoadPriceCall = false, checkoutError, quoteEventParams) {
    const error = getError(checkoutError);

    if (error) {
        const validDates = error.type !== types.DATES;
        const validGuests = error.type !== types.GUESTS;

        store.dispatch(
            listingActions.datesAreValid({
                datesValid: validDates
            })
        );

        store.dispatch(listingActions.guestsAreValid(validGuests));

        const {listingReducer, tripDetailsReducer, systemReducer} = store.getState();
        if (!validDates || !validGuests) {
            const defaultPricingKey = createDefaultPricingKey(tripDetailsReducer, systemReducer);
            store.dispatch(listingActions.pricingInfoReset(defaultPricingKey));
        }

        let newQuoteEventParams;
        if (quoteEventParams) {
            // eslint-disable-next-line camelcase
            const {eventstartdate: date_start, eventenddate: date_end, ...rest} = quoteEventParams;
            newQuoteEventParams = {date_start, date_end, ...rest};
        } else {
            newQuoteEventParams = quoteEventParams;
        }
        store.dispatch(edapActions.triggerEdapEvent('quote.failure', quoteEventParams));
        store.dispatch(
            edapActions.triggerEdapEvent('price_quote_request.failed', newQuoteEventParams)
        );

        store.dispatch(cancellationPolicyActions.failedOnQuoteLoad());

        reportError({name: 'quote failure'});
        reportError({name: 'price_quote_request failed'});

        const graphqlRequestMarker =
            checkoutError && checkoutError.graphqlRequestMarker
                ? checkoutError.graphqlRequestMarker
                : null;

        logToServer({
            message: 'quote.attempt',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });

        logToServer({
            message: 'price_quote_request.initiated',
            quoteSuccess: false,
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            errorCode: checkoutError ? checkoutError.errorCode : 'UNDEFINED',
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            graphqlRequestMarker,
            ...quoteEventParams
        });
    }
}
/* eslint-enable complexity */

function getPriceSummaryObjectByType(priceSummary, priceSummarySecondary, type) {
    if (priceSummary && priceSummary.priceTypeId === type) {
        return {
            amount: priceSummary.formattedAmount,
            label: priceSummary.pricePeriodDescription
        };
    }
    if (priceSummarySecondary && priceSummarySecondary.priceTypeId === type) {
        return {
            amount: priceSummarySecondary.formattedAmount,
            label: priceSummarySecondary.pricePeriodDescription
        };
    }
    return {amount: '', label: ''};
}

function getPriceSummaryAmountByType(priceSummary, priceSummarySecondary, type) {
    if (priceSummary && priceSummary.priceTypeId === type) {
        return priceSummary.formattedAmount;
    }
    if (priceSummarySecondary && priceSummarySecondary.priceTypeId === type) {
        return priceSummarySecondary.formattedAmount;
    }
    return '';
}

/* eslint-disable complexity */
function handleGraphQlResult(
    store,
    pageLoadPriceCall,
    priceDetails,
    arrival,
    departure,
    adults,
    children,
    currency,
    pets,
    clientPriceFetchTime,
    displayLocale,
    defaultadults
) {
    const {listingReducer} = store.getState();

    const priceDetailsData = priceDetails.priceDetails || {};
    const priceDetailsError = priceDetails.originalErrorCode;
    const quoteEventParams = priceDetailsData.edapQuoteSuccess
        ? JSON.parse(priceDetailsData.edapQuoteSuccess)
        : {};
    quoteEventParams.defaultadults = defaultadults;
    if (priceDetailsError || isempty(priceDetailsData)) {
        const checkoutError = {
            errorCode: priceDetailsError || 'API_BROKEN'
        };
        if (priceDetails.graphqlRequestMarker) {
            checkoutError.graphqlRequestMarker = priceDetails.graphqlRequestMarker;
        }

        const quoteFailureEventParams = {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            eventrequestmarker: priceDetails.graphqlRequestMarker,
            eventerrorcode: checkoutError.errorCode.toString()
        };
        handleError(store, pageLoadPriceCall, checkoutError, quoteFailureEventParams);
    } else {
        store.dispatch(
            cancellationPolicyActions.updateCancellationPolicy(priceDetailsData.cancellationPolicy)
        );
        store.dispatch(edapActions.triggerEdapEvent('quote.success', quoteEventParams));
        store.dispatch(
            edapActions.triggerEdapEvent('price_quote_request.succeeded', quoteEventParams)
        );

        reportSuccess({name: 'quote success'});
        reportSuccess({name: 'price_quote_request succeeded'});

        logToServer({
            message: 'quote.attempt',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });

        logToServer({
            message: 'price_quote_request.initiated',
            pageLoadPriceCall: pageLoadPriceCall.toString(),
            clientPriceFetchTime,
            quoteSuccess: true,
            eventHasPets: pets,
            ...quoteEventParams,
            ownerManaged: listingReducer.ownerManaged,
            integratedPropertyManager: listingReducer.integratedPropertyManager,
            displayLocale,
            graphqlRequestMarker: priceDetails.graphqlRequestMarker
        });
    }
}
/* eslint-enable complexity */

/* eslint-disable complexity */
const pricingHelper = async ({
    store,
    pointOfSaleId,
    listingId = '',
    displayLocale = '',
    arrival = '',
    departure = '',
    adults = 0,
    children = 0,
    pets = 0,
    defaultadults = 'false',
    currency = '',
    loadingRate = () => {},
    loadedRate = () => {},
    pageLoadPriceCall = false,
    unitApiUrl = ''
}) => {
    reportSuccess({name: 'quote attempt'});
    reportSuccess({name: 'price_quote_request initiated'});

    store.dispatch(loadingRate(true));
    store.dispatch(
        edapActions.triggerEdapEvent('quote.attempt', {
            eventstartdate: arrival,
            eventenddate: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );
    store.dispatch(
        edapActions.triggerEdapEvent('price_quote_request.initiated', {
            date_start: arrival,
            date_end: departure,
            eventnumadults: `${adults}`,
            eventnumchildren: `${children}`,
            defaultadults
        })
    );

    const state = store.getState();
    const abacus = state.abacusTestsReducer;

    const useQuoteQuery = evaluateExperiment({
        abacus,
        name: VRBO_PDP_R2D2_QUERY,
        variant: 2
    });
    const compareQuoteQuery = evaluateExperiment({
        abacus,
        name: VRBO_PDP_R2D2_QUERY,
        variant: 1
    });

    let graphqlPricingArgs;
    let graphqlPricingResult;
    let graphqlPriceSummaryResult = {};

    const startPricingCall = Date.now();

    try {
        if (useQuoteQuery) {
            const graphqlQuoteResult = await quoteGraphql(
                {
                    unit: unitApiUrl,
                    listingId,
                    pointOfSaleId,
                    estimatedCurrency: currency,
                    arrivalDate: arrival,
                    departureDate: departure,
                    adults,
                    childAges: initializeChildAges(children),
                    pets
                },
                displayLocale,
                store
            );

            // Use graphqlQuote for pricing result in a minimally invasive way
            graphqlPricingResult = {
                ...graphqlQuoteResult,
                priceDetails:
                    graphqlQuoteResult.quote && graphqlQuoteResult.quote.priceDetails
                        ? graphqlQuoteResult.quote.priceDetails
                        : {}
            };

            // Load damage policy
            if (
                graphqlQuoteResult &&
                graphqlQuoteResult.quote &&
                graphqlQuoteResult.quote.policies
            ) {
                const damagePolicy = graphqlQuoteResult.quote.policies.find((policy) => {
                    return policy.type === 'DAMAGE_LIABILITY';
                });
                store.dispatch({
                    type: actions.UPDATE_DAMAGE_POLICY,
                    payload: {
                        damagePolicy
                    }
                });
            }
        } else {
            graphqlPricingArgs = {
                unit: unitApiUrl,
                arrivalDate: arrival,
                departureDate: departure,
                adults,
                children,
                pets,
                listingRef: listingId,
                currency
            };
            const graphqlPriceSummaryArgs = {
                unitId: unitApiUrl,
                arrivalDate: arrival,
                departureDate: departure,
                listingUrl: listingId,
                adults,
                children,
                petIncluded: pets
            };

            [graphqlPricingResult, graphqlPriceSummaryResult] = await Promise.all([
                pricingGraphql(graphqlPricingArgs, displayLocale, store),
                priceSummaryGraphql(graphqlPriceSummaryArgs, displayLocale)
            ]);
        }

        const pricingKey = {
            arrival,
            departure,
            adults,
            children,
            currency,
            pets
        };
        let clientPriceFetchTime = graphqlPricingResult.endTime - startPricingCall;

        if (graphqlPricingResult.errorCode && !graphqlPricingResult.originalErrorCode) {
            graphqlPricingResult.originalErrorCode = graphqlPricingResult.errorCode;
        }

        handleGraphQlResult(
            store,
            pageLoadPriceCall,
            graphqlPricingResult,
            arrival,
            departure,
            adults,
            children,
            currency,
            pets,
            clientPriceFetchTime,
            displayLocale,
            defaultadults
        );
        // Load price details

        store.dispatch(
            loadedRate(
                {},
                graphqlPricingResult,
                pricingKey,
                graphqlPriceSummaryResult ? graphqlPriceSummaryResult.listing : {}
            )
        );
    } catch (err) {
        // The only exception that should be caught here is for the primary pricing call.
        // The secondary compared call was wrapped in the 'whoCaresIfFailedPromise' Promise.
        const checkoutError = {
            id: 'UNKNOWN',
            errorCode: 'INTERNAL_ERROR',
            message: `Pricing call error: ${err}`
        };

        handleError(store, pageLoadPriceCall, checkoutError, {});

        // If graphql was primary, fill in the empty structure's error code.
        store.dispatch(loadedRate({[listingId]: {checkoutError}}, {}, checkoutError));
        store.dispatch(loadingRate(false));
    }

    // Run comparator for priceDetails vs. quote.priceDetails
    /* eslint-disable no-undef, promise/catch-or-return */
    if (compareQuoteQuery && graphqlPricingResult) {
        const graphqlQuoteArgs = {
            unit: unitApiUrl,
            listingId,
            pointOfSaleId,
            estimatedCurrency: currency,
            arrivalDate: arrival,
            departureDate: departure,
            adults,
            childAges: initializeChildAges(children),
            pets
        };
        quoteGraphql(graphqlQuoteArgs, displayLocale, store).then((graphqlQuoteResult) => {
            pricingHelperQueryComparator({
                graphqlPricingArgs,
                graphqlPricingResult,
                graphqlQuoteArgs,
                graphqlQuoteResult,
                isLoggedIn: state.travelerFeaturesReducer.isLoggedIn
            });
        });
    }
    /* eslint-disable no-undef, promise/catch-or-return */
};
/* eslint-enable complexity */

export default pricingHelper;
export {
    getError,
    handleError,
    handleGraphQlResult,
    pricingHelper,
    createDefaultPricingKey,
    getPriceSummaryObjectByType,
    getPriceSummaryAmountByType
};
